import { isGhostAccess, userCanSeeCredits, userCanSeeTecselect } from '@ab-core-functions/user-permission';
import { BreakpointsMinQuery } from '@ab-core/breakpoints';
import { DropdownOverlay } from '@ab-core/dropdownoverlay';
import { ROUTES, useMediaQuery } from '@ab-core/functions';
import { useAuth } from '@ab-core/hooks';
import { Link, LINK_VARIANT } from '@ab-core/link';
import { LoginBox } from '@ab-core/loginbox';
import { Search } from '@ab-core/search';
import { navigationStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import Text, { FontTypes } from '@ab-core/text';
import { UserSettings } from '@ab-core/usersettings';
import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import { PointsOverview } from '../components';
import { Brand } from '../components/brand';
import { Cart } from '../components/cart';
import { ElementContainer } from '../components/elementContainer/elementContainer';
import { SalesView } from '../components/salesView';
import { DesktopBarWrapper, LinkWrapper, VerticalDivider } from './index.styled';

type DesktopProps = {
    searchString?: string;
    pathname: string;
};

export const Desktop: FC<DesktopProps> = (props) => {
    const { searchString, pathname } = props;
    const { user } = useAuth();
    const activeGhostAccess = isGhostAccess();
    const collectionButtonRefObject = useRef<HTMLDivElement>(null);
    const collectionState = navigationStore.getters.useCollectionNavigation();

    const isDesktopLarge = useMediaQuery(BreakpointsMinQuery.xl);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const openUserDropdown = navigationStore.getters.useUserDropdownNavigation();
    useEffect(() => {
        navigationStore.setters.setCollectionButtonRef(collectionButtonRefObject);
    }, [collectionButtonRefObject]);

    const getIconName = () => {
        return activeGhostAccess ? 'Ghost' : user ? 'AccountLoggedIn' : 'Account';
    };

    const canSeeCredits = userCanSeeCredits();
    const canSeeTecselect = userCanSeeTecselect();
    const canSeePoints = canSeeCredits || canSeeTecselect;

    return (
        <DesktopBarWrapper>
            <Brand />
            {user && (
                <>
                    <VerticalDivider />
                    <ElementContainer
                        {...getTestIdProp('open-collection-button')}
                        ref={collectionButtonRefObject}
                        iconName={!collectionState ? 'Collection' : 'Close'}
                        content={isDesktopLarge ? 'Sortiment' : ''}
                        onClick={() => navigationStore.setters.setCollectionNavigation(!collectionState)}
                    />
                </>
            )}
            <Search searchString={searchString} />
            <SalesView />

            <div className="flex" ref={wrapperRef}>
                {canSeePoints && (
                    <>
                        <VerticalDivider />
                        <PointsOverview onClick={() => navigationStore.setters.setUserDropdownNavigation(false)} />
                    </>
                )}

                <VerticalDivider />
                <ElementContainer
                    {...getTestIdProp('user-settings-dropdown-button')}
                    iconName={getIconName()}
                    content={user ? `${user?.firstName} ${user?.lastName}` : 'Anmelden / Registrieren'}
                    onClick={() => navigationStore.setters.setUserDropdownNavigation(!openUserDropdown)}
                    suffixIconName={openUserDropdown ? 'ChevronUp' : 'ChevronDown'}
                    backgroundColor={activeGhostAccess ? 'warning' : 'white'}
                    fontColor={activeGhostAccess ? 'white' : 'black'}
                />
                <VerticalDivider />
                <div className="px-medium2 pt-medium pb-medium2">
                    <Cart pathname={pathname} />
                </div>
                <VerticalDivider />
                <DropdownOverlay
                    parentRef={wrapperRef}
                    open={!!openUserDropdown}
                    onClose={() => navigationStore.setters.setUserDropdownNavigation(false)}
                >
                    <div {...getTestIdProp('user-settings-dropdown')} className="px-small3">
                        {user ? <UserSettings /> : <LoginBox />}
                    </div>
                </DropdownOverlay>
            </div>
            <LinkWrapper>
                <Link variant={LINK_VARIANT.LINK_ONLY} to={ROUTES.START} target={'_blank'}>
                    <Text type={FontTypes.Button} color="white">
                        zur Website
                    </Text>
                </Link>
            </LinkWrapper>
        </DesktopBarWrapper>
    );
};
